<template>
    <vvo v-slot="v">
        <div>
            <div class="target-info mb-2">
                <span class="target-label">{{ $t("대상 스케줄") }}</span>
                <span class="target-count">{{ targetSchedules.length }}{{ $t("건") }}</span>
                <span class="target-names">{{ targetScheduleNames }}</span>
            </div>
            <div class="apply-mode-row mb-2">
                <span class="target-label">{{ $t("적용 방식") }}</span>
                <select v-model="applyMode" class="form-control apply-mode-select">
                    <option value="add">{{ $t("추가") }}</option>
                    <option value="replace">{{ $t("덮어쓰기") }}</option>
                </select>
            </div>
            <p class="help-text mb-2">{{ applyModeHelpText }}</p>
            <TableList
                ref="table-list"
                :title="''"
                :columns="columns"
                :rows="exceptDays"
                :fixedHeader="false"
                :useFilter="false"
                :transCodes="transCodes"
                :keyField="'exceptDt'"
                :controls="controls"
                :mode="'edit'"
                :useMaxHeight="true"
                :customMaxHeight="'320px'"
                :toggleFields="['enabled']"
                :isSort="false"
                @row-click="handleListItemClick"
                @button-click="handleListButtonClick"
                @toggle-changed="handleItemToggle"
            />

            <b-modal modal-class="sch-modal" ref="ExceptionDayDetailModal" centered @hidden="closeDetailModal">
                <template #modal-header>
                    <h5>{{ exceptIdx !== null ? $t("예외일 편집") : $t("예외일 추가") }}</h5>
                </template>
                <template #modal-footer>
                    <div style="display: flex; flex: 1">
                        <button
                            type="button"
                            class="btn btn-primary"
                            style="flex: 1; margin-right: 0.5rem"
                            @click="onValidate(v, handleAddListItem)"
                        >
                            {{ $t("저장") }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            style="flex: 1; margin-left: 0.5rem"
                            @click="closeDetailModal"
                        >
                            {{ $t("닫기") }}
                        </button>
                    </div>
                </template>
                <ExceptionDayDetailPanel ref="ExceptionDayDetailPanel" :data="exceptDetailData" :mode="'edit'" />
            </b-modal>

            <b-modal modal-class="sch-modal" ref="ExceptionDayCopyModal" @hidden="closeExceptionDayCopy">
                <template #modal-header>
                    <h5>{{ $t("예외일 불러오기") }}</h5>
                </template>
                <template #modal-footer>
                    <div style="display: flex; flex: 1">
                        <button
                            type="button"
                            class="btn btn-primary"
                            style="margin-right: 0.5rem; flex: 1"
                            @click="addExceptCopyList"
                        >
                            {{ $t("저장") }}
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            style="margin-left: 0.5rem; flex: 1"
                            @click="closeExceptionDayCopy"
                        >
                            {{ $t("닫기") }}
                        </button>
                    </div>
                </template>
                <ExceptionDayCopyPanel ref="ExceptionDayCopyPanel" :data="exceptDetailData" />
            </b-modal>
        </div>
    </vvo>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import TableList from "@views/component/v2.1/ListDetailView/TableList.vue";
import ExceptionDayDetailPanel from "./ExceptionDayDetailPanel.vue";
import ExceptionDayCopyPanel from "./ExceptionDayCopyPanel.vue";
import moment from "moment";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    props: {
        targetSchedules: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        TableList,
        ExceptionDayDetailPanel,
        ExceptionDayCopyPanel,
    },
    data() {
        return {
            exceptIdx: null,
            exceptDays: [],
            applyMode: "add",
            saving: false,
            controls: [
                { event: "copyExcept", label: "예외일 불러오기", class: "btn-secondary" },
                { event: "delete", label: "-", class: "btn-danger" },
                { event: "regist", label: "+", class: "btn-primary" },
            ],
            transCodes: [
                {
                    field: "enabled",
                    codes: this.$store.state.commonCodes.enabled,
                },
            ],
            columns: [
                {
                    label: this.$t("예외일명"),
                    field: "exceptName",
                    type: "text",
                },
                {
                    label: this.$t("예외일자"),
                    field: "exceptDt",
                    type: "text",
                },
                {
                    label: this.$t("사용여부"),
                    field: "enabled",
                    type: "text",
                },
            ],
            popupMessages,
        };
    },
    computed: {
        exceptDetailData() {
            return {
                schdCtrlIdx: this.targetSchedules[0] ? this.targetSchedules[0].schdCtrlIdx : null,
            };
        },
        targetScheduleNames() {
            return this.targetSchedules.map((item) => item.schdCtrlName).join(", ");
        },
        applyModeHelpText() {
            if (this.applyMode === "replace") {
                return this.$t(
                    "아래 예외일 목록으로 선택한 스케줄의 예외일을 덮어씁니다. 목록을 비운 채 저장하면 기존 예외일이 모두 삭제됩니다.",
                );
            }
            return this.$t(
                "아래 예외일을 선택한 스케줄에 추가합니다. 이미 같은 날짜가 있으면 건너뜁니다.",
            );
        },
    },
    methods: {
        reset() {
            this.exceptIdx = null;
            this.exceptDays = [];
            this.applyMode = "add";
            this.saving = false;
        },
        async handleListButtonClick(event, options) {
            switch (event) {
                case "regist":
                    await this.registNew();
                    break;
                case "delete":
                    await this.deleteChecked();
                    break;
                case "copyExcept":
                    await this.showExceptionDayCopy();
                    break;
                default:
                    console.warn("Unhandled Component Except. event:", event, "options:", options);
                    break;
            }
        },
        async handleListItemClick(row, index, toggle, check) {
            if (toggle || check) return;
            this.exceptIdx = index;
            const findExcept = this.exceptDays.find((item) => item.exceptDt == row.exceptDt);
            await this.$refs.ExceptionDayDetailModal.show();
            await this.$refs.ExceptionDayDetailPanel.setData(findExcept);
        },
        async registNew() {
            this.exceptIdx = null;
            this.$refs.ExceptionDayDetailModal.show();
            this.$nextTick(() => {
                this.$refs.ExceptionDayDetailPanel.setData(null);
            });
        },
        async addExceptCopyList() {
            const except = this.$refs.ExceptionDayCopyPanel.getData();
            // 공휴일/스케줄을 선택하지 않아도 저장 가능 (목록 변경 없이 닫힘)
            if (!this.isEmpty(except)) {
                except.forEach((list) => {
                    if (!this.exceptDays.some((item) => item.exceptDt == list.exceptDt)) {
                        this.exceptDays.push({
                            exceptName: list.exceptName,
                            exceptDt: list.exceptDt,
                            enabled: list.enabled || "Y",
                        });
                    }
                });
                this.exceptDays.sort((a, b) => new Date(a.exceptDt) - new Date(b.exceptDt));
            }
            await this.closeExceptionDayCopy();
        },
        async deleteChecked() {
            try {
                const checked = this.$refs["table-list"].getCheckedRows();
                if (!this.isEmpty(checked)) {
                    const confirm = await this.alertConfirmWarning(popupMessages.COMMON_DELETE_CONFIRM_POPUP_MESSAGE);
                    if (!confirm.value) return;
                    this.exceptDays = this.exceptDays.filter(
                        (except) => !checked.find((checkedExcept) => checkedExcept.exceptDt === except.exceptDt),
                    );
                } else {
                    this.alertNoti(popupMessages.COMMON_DELETE_NOTI_POPUP_MESSAGE);
                }
            } catch (e) {
                this.alertNoti(popupMessages.COMMON_REMOVE_ERROR_POPUP_MESSAGE);
                console.error(e);
            }
        },
        async handleAddListItem() {
            const except = this.$refs.ExceptionDayDetailPanel.getData();
            if (except) {
                if (except.exceptDt) {
                    except.exceptDt = moment(except.exceptDt).format("YYYY-MM-DD");
                }
                if (this.exceptIdx !== null) {
                    this.$set(this.exceptDays, this.exceptIdx, except);
                    this.exceptIdx = null;
                } else {
                    const check = this.exceptDays.find((item) => item.exceptDt === except.exceptDt);
                    if (check) {
                        this.alertNoti(this.$t("이미 추가된 예외일자입니다."));
                    } else {
                        this.$set(this.exceptDays, this.exceptDays.length, except);
                    }
                }
                this.exceptDays.sort((a, b) => new Date(a.exceptDt) - new Date(b.exceptDt));
            }
            await this.closeDetailModal();
        },
        closeDetailModal() {
            this.exceptIdx = null;
            this.$refs.ExceptionDayDetailModal.hide();
        },
        handleItemToggle(key, field, status) {
            const found = this.exceptDays.find((item) => item.exceptDt === key);
            if (found) found.enabled = status;
        },
        showExceptionDayCopy() {
            this.$refs.ExceptionDayCopyModal.show();
        },
        closeExceptionDayCopy() {
            this.$refs.ExceptionDayCopyModal.hide();
        },
        normalizeRepeatConfig(repeatConfig) {
            const next = { ...(repeatConfig || {}) };
            Object.keys(next).forEach((key) => {
                if (!next[key]) delete next[key];
                else next[key] = "Y";
            });
            return next;
        },
        buildExceptConfig(currentExcepts, actionExcepts, mode) {
            const mapped = (actionExcepts || []).map((item) => ({
                exceptName: item.exceptName,
                exceptDt: item.exceptDt,
                enabled: item.enabled || "Y",
            }));

            if (mode === "replace") {
                return mapped;
            }

            const current = Array.isArray(currentExcepts) ? [...currentExcepts] : [];
            const existSet = new Set(current.map((item) => item.exceptDt));
            mapped.forEach((item) => {
                if (!existSet.has(item.exceptDt)) {
                    current.push(item);
                    existSet.add(item.exceptDt);
                }
            });
            return current.sort((a, b) => new Date(a.exceptDt) - new Date(b.exceptDt));
        },
        async apply() {
            if (this.saving) return { success: false };

            if (this.isEmpty(this.targetSchedules)) {
                this.alertNoti(popupMessages.SCHEDULE_CONTROL_EXCEPT_BATCH_NO_SCHEDULE_MESSAGE);
                return { success: false };
            }

            const scheduleCount = this.targetSchedules.length;
            const exceptCount = this.exceptDays.length;
            const isReplace = this.applyMode === "replace";

            if (!isReplace && exceptCount === 0) {
                this.alertNoti(popupMessages.SCHEDULE_CONTROL_EXCEPT_BATCH_NO_DAY_MESSAGE);
                return { success: false };
            }

            let confirmMessage;
            if (isReplace) {
                confirmMessage =
                    exceptCount === 0
                        ? `선택한 ${scheduleCount}개 스케줄의 기존 예외일이 모두 삭제됩니다.\n목록이 비어 있어 예외일이 전부 지워집니다.\n계속하시겠습니까?`
                        : `선택한 ${scheduleCount}개 스케줄의 기존 예외일이 모두 덮어쓰여집니다.\n현재 목록(${exceptCount}건)으로 교체됩니다.\n계속하시겠습니까?`;
            } else {
                confirmMessage = `선택한 ${scheduleCount}개 스케줄에 예외일 ${exceptCount}건을 추가합니다.\n같은 날짜는 건너뜁니다.\n계속하시겠습니까?`;
            }

            const confirm = await this.alertConfirmWarning(confirmMessage);
            if (!confirm || !confirm.value) return { success: false };

            this.saving = true;
            let successCount = 0;
            let failCount = 0;

            try {
                const actionExcepts = (this.exceptDays || []).map((item) => ({
                    exceptName: item.exceptName,
                    exceptDt: item.exceptDt,
                    enabled: item.enabled || "Y",
                }));

                for (const target of this.targetSchedules) {
                    try {
                        const detailResult = await backEndApi.equipControl.getEquipCtrlSchDetail(target.schdCtrlIdx);
                        if (detailResult.status != 200 || !detailResult.data) {
                            failCount += 1;
                            continue;
                        }

                        const detail = { ...detailResult.data };
                        detail.exceptConfig = this.buildExceptConfig(
                            detail.exceptConfig,
                            actionExcepts,
                            this.applyMode,
                        ).map((item) => ({
                            ...item,
                            schdCtrlIdx: target.schdCtrlIdx,
                        }));
                        detail.repeatConfig = this.normalizeRepeatConfig(detail.repeatConfig);

                        if (detail.startDate) {
                            detail.startDate = moment(detail.startDate).format("YYYY-MM-DD");
                        }
                        if (detail.endDate) {
                            detail.endDate = moment(detail.endDate).format("YYYY-MM-DD");
                        }

                        const saveResult = await backEndApi.equipControl.saveEquipCtrlSch(detail);
                        if (saveResult.status == 200) successCount += 1;
                        else failCount += 1;
                    } catch (e) {
                        console.error("Fail to apply except days", target, e);
                        failCount += 1;
                    }
                }

                if (failCount === 0) {
                    await this.alertNoti(popupMessages.SCHEDULE_CONTROL_EXCEPT_BATCH_SUCCESS_MESSAGE);
                    return { success: true, successCount, failCount };
                }

                this.alertWarning(
                    `${this.$t("예외일 설정 결과")}: ${this.$t("성공")} ${successCount}, ${this.$t(
                        "실패",
                    )} ${failCount}`,
                );
                return { success: successCount > 0, successCount, failCount };
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.target-info {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 12px;
}

.target-label {
    font-weight: bold;
}

.target-count {
    color: #546e7a;
    font-weight: 600;
}

.target-names {
    color: #666;
}

.apply-mode-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
}

.apply-mode-select {
    width: auto;
    min-width: 180px;
    height: 30px;
    padding: 2px 8px;
    font-size: 12px;
}

.help-text {
    margin: 0;
    color: #777;
    font-size: 12px;
}
</style>
